// src/pages/user/UserEditPage.tsx
// Edit form with all User fields.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useUser, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, CardFooter, ErrorAlert } from '@react/components';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { validateUserUpdate } from '../../features/user/logic';
import { UserStatusValues } from '@core/types/user';
import type { User } from '@core/types/user';
import { Select } from '@/components/Select';

export const UserEditPage = observer(() => {
  const userStore = useUser();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: '', username: '', phone: '', status: 'USER_STATUS_ACTIVE', emailVerified: false, phoneVerified: false, twoFactorEnabled: false, inviteCode: '' });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { if (id) userStore.GetUser(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const user = userStore.currentItem as User | null;
    if (user && !loaded) {
      setForm({ email: user.email ?? '', username: user.username ?? '', phone: user.phone ?? '', status: user.status ?? 'USER_STATUS_ACTIVE', emailVerified: user.email_verified ?? false, phoneVerified: user.phone_verified ?? false, twoFactorEnabled: user.two_factor_enabled ?? false, inviteCode: user.invite_code ?? '' });
      setLoaded(true);
    }
  }, [userStore.currentItem, loaded]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSave = async () => {
    const validation = validateUserUpdate(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      const current = userStore.currentItem as User;
      await userStore.UpdateUser(id ?? '', { user: { ...current, email: form.email, username: form.username, phone: form.phone, status: form.status, email_verified: form.emailVerified, phone_verified: form.phoneVerified, two_factor_enabled: form.twoFactorEnabled, invite_code: form.inviteCode || undefined } as never });
      navigate(`/users/${id}`);
    } catch (err) {
      setErrors({ form: err });
    }
  };

  if (userStore.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <Link to={`/users/${id}`} className="text-sm text-primary hover:text-primary/80">{t('common.back')}</Link>
        <h1 className="sr-only">{t('user.edit')}</h1>
      </div>
      {errors.form ? <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} /> : null}
      <Card>
        <CardHeader><CardTitle>{t('user.details')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input label={t('auth.email')} type="email" value={form.email} onChange={handleChange('email')} error={errors.email as string | undefined} required />
          <Input label={t('auth.username')} value={form.username} onChange={handleChange('username')} error={errors.username as string | undefined} required />
          <Input label={t('user.phone')} type="tel" value={form.phone} onChange={handleChange('phone')} />
          <div>
            <label className="label">{t('common.status')}</label>
            <Select value={form.status} onChange={handleChange('status')}>
              {UserStatusValues.map((s) => <option key={s} value={s}>{s.replace('USER_STATUS_', '')}</option>)}
            </Select>
          </div>
          <ToggleSwitch label={t('user.emailVerified')} checked={form.emailVerified} onChange={(v) => setForm((p) => ({ ...p, emailVerified: v }))} />
          <ToggleSwitch label={t('user.phoneVerified')} checked={form.phoneVerified} onChange={(v) => setForm((p) => ({ ...p, phoneVerified: v }))} />
          <ToggleSwitch label={t('user.twoFactor')} checked={form.twoFactorEnabled} onChange={(v) => setForm((p) => ({ ...p, twoFactorEnabled: v }))} />
          <Input label={t('user.inviteCode')} value={form.inviteCode} onChange={handleChange('inviteCode')} />
        </CardContent>
        <CardFooter>
          <Button variant="secondary" onClick={() => navigate(`/users/${id}`)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} isLoading={userStore.isSaving} className="ml-2">{t('common.saveChanges')}</Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default UserEditPage;
